/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Input, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { IBoton, IPlantillaBotonera } from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import {
  botonesDePantalla,
  ID_PLANTILLA_MANUAL,
  pantallasDe,
  plantillaInicial,
  reemplazarBoton,
  sincronizarPantallas,
} from '../../../../auxiliares/plantillas/plantilla-manual';
import { BotonElegible } from '../plantilla-manual/plantilla-manual-editor.component';
import {
  BOTONERA_OPCIONES,
  BOTONERA_SPECS,
  BotoneraTemplateId,
  textoHint,
} from '../../../../auxiliares/plantillas/plantillas-spec';
import {
  TemplateCustom,
  templateUsaSlot,
} from '../../../../auxiliares/plantillas/templates-custom';
import { carpetaCategoria, ClientesService } from '../../clientes.service';
import { crearGrupoBoton } from '../../provisionador/boton-form/boton-form.component';
import {
  construirPreviewVM,
  PreviewBoton,
  PreviewVM,
} from '../../provisionador/preview/preview-vm';
import {
  CategoriaEditable,
  SLOTS_LEGACY,
  SLOTS_NATIVOS,
  TMP_BTN,
} from '../cliente-form.model';

interface SlotImagen {
  control: string;
  label: string;
  hint: string;
  /** El template del cliente NO dibuja esta imagen: se guarda pero no se ve en la app. */
  sinUso?: boolean;
}

/**
 * Etiquetas de los overrides de imagen cuando el template es personalizado: no hay spec de la
 * que sacar tamaños, pero el nativo los aplica igual. Mismo orden que `SLOTS_NATIVOS`.
 */
const LABELS_SIN_SPEC: Record<string, string> = {
  arriba: 'Logo / banner superior',
  abajo: 'Banner inferior',
  fondo: 'Fondo de pantalla',
  sirena: 'Sirena',
  reflector: 'Reflector',
  adelante: 'Botón Adelante',
  atras: 'Botón Atrás',
};

/**
 * Editor del DETALLE de una categoría del cliente, para el form unificado.
 *
 * Equivalente a `categoria-editor` (que se sigue usando en el provisionador y en
 * `crear-editar-cliente`), pero con tres diferencias de fondo:
 *  - el armado se presenta como PANTALLA 1 / 2 / 3, no como principales/secundarios/otros;
 *  - las imágenes del nativo y las de la web legacy quedan separadas, y este componente es el
 *    ÚNICO que las escribe (antes las escribían la tab General y la tab App Nativa en paralelo);
 *  - el catálogo de botones lo inyecta el padre (una sola suscripción para todas las categorías).
 *
 * Opera sobre la `CategoriaEditable` que recibe POR REFERENCIA. No llama a ningún servicio de
 * guardado: eso lo orquesta `ClienteFormService`.
 */
@Component({
  selector: 'app-categoria-detalle',
  templateUrl: './categoria-detalle.component.html',
  styleUrls: ['./categoria-detalle.component.scss'],
  standalone: false,
})
export class CategoriaDetalleComponent {
  private fb = inject(UntypedFormBuilder);
  private clientesService = inject(ClientesService);
  private helper = inject(HelperService);

  @Input() editable!: CategoriaEditable;
  /** Nombre del cliente: define la ruta de subida de imágenes. */
  @Input() nombreCliente = '';
  /** Valor del form del cliente (para el preview: imagenes base, configuracion, colorTema). */
  @Input() clienteFormValue: any = {};
  @Input() sirenas = false;
  /** Catálogo completo de botones, para "usar uno ya creado". Lo carga el padre. */
  @Input() botonesCatalogo: IBoton[] = [];
  /**
   * Template hecho a mano que le corresponde al cliente por su bundle ID, si tiene. Cuando viene,
   * la botonera NO se elige: la pantalla la define la app nativa. Lo resuelve el padre con
   * `templateCustomDeCliente()`.
   */
  @Input() templateCustom?: TemplateCustom;

  public loading = false;
  public botoneraOpciones = BOTONERA_OPCIONES;
  public slotsLegacy = SLOTS_LEGACY;
  private contadorTmp = 0;

  get grupo(): UntypedFormGroup {
    return this.editable.grupo;
  }
  get botonesControls(): UntypedFormGroup[] {
    return (this.editable.botones?.controls ?? []) as UntypedFormGroup[];
  }
  /**
   * Botonera genérica elegida en ESTA categoría. `null` = no elige ninguna, y es el valor que
   * tienen que tener los clientes con template hecho a mano: en el nativo la categoría GANA sobre
   * el cliente (`InicioViewModel`: `cat.inicioTemplate ?: cli.inicioTemplate`), así que poner una
   * genérica acá le reemplaza el layout personalizado.
   */
  get botoneraPropia(): BotoneraTemplateId | null {
    return (this.grupo?.get('inicioTemplate')?.value as BotoneraTemplateId) || null;
  }

  /** Familia efectiva: la de la categoría; si no elige, la del cliente. Igual que el nativo. */
  get botoneraId(): BotoneraTemplateId | undefined {
    return (
      this.botoneraPropia ??
      ((this.clienteFormValue?.inicioTemplate as BotoneraTemplateId) || undefined)
    );
  }

  /**
   * El cliente tiene template hecho a mano: la botonera no se elige acá. Se decide por bundle ID,
   * igual que en el nativo (`Clientes.forBundleId`).
   */
  get bloqueado(): boolean {
    return !!this.templateCustom;
  }

  public readonly idManual = ID_PLANTILLA_MANUAL;

  /** La categoría usa una plantilla armada a mano (`plantillaBotonera`). */
  get esManual(): boolean {
    return (
      !this.bloqueado &&
      this.grupo?.get('inicioTemplate')?.value === ID_PLANTILLA_MANUAL
    );
  }

  get plantillaManual(): IPlantillaBotonera | null {
    return this.grupo?.get('plantillaBotonera')?.value ?? null;
  }

  /** Pantalla que se está editando/previsualizando en la plantilla manual (0..2). */
  public pantallaManual = 0;

  /**
   * Guarda la plantilla y deja el reparto de botones (`idsPantalla1/2/3`) igual a lo que muestra:
   * cada botón pasa a la pantalla donde se lo puso en una pieza.
   */
  public setPlantilla(p: IPlantillaBotonera | null): void {
    this.grupo?.patchValue({ plantillaBotonera: p });
    this.grupo?.markAsDirty();
    if (this.esManual) {
      sincronizarPantallas(p, [
        this.editable.idsPantalla1,
        this.editable.idsPantalla2,
        this.editable.idsPantalla3,
      ]);
    }
  }

  /** Botones de la categoría para elegir en las piezas. */
  get botonesElegibles(): BotonElegible[] {
    return this.botonesControls.map((g) => {
      const v = g.value;
      return {
        id: String(v._tmpId),
        nombre:
          (v.nombre || v.texto || 'Botón sin nombre') +
          (v.variante ? ` (${v.variante})` : ''),
        funcion: v.funcion,
        color: v.color,
      };
    });
  }

  /** Mismos botones, indexados por id, para el preview. */
  get botonesPreview(): Record<string, PreviewBoton> {
    const out: Record<string, PreviewBoton> = {};
    for (const g of this.botonesControls) {
      const v = g.value;
      out[String(v._tmpId)] = {
        nombre: v.nombre,
        texto: v.texto,
        funcion: v.funcion,
        color: v.color,
        urlImagen: v.urlImagen,
      };
    }
    return out;
  }

  /** Nombres de los botones que no están en ninguna pieza de la plantilla manual. */
  get botonesSinUsar(): string[] {
    const usados = new Set(
      pantallasDe(this.plantillaManual).flatMap((p) => botonesDePantalla(p)),
    );
    return this.botonesElegibles
      .filter((b) => !usados.has(b.id))
      .map((b) => b.nombre);
  }

  /** ¿La familia efectiva es una de las genéricas? Solo esas se pueden previsualizar. */
  get esGenerica(): boolean {
    if (this.bloqueado) return false;
    const fam = this.botoneraId;
    return !!fam && !!BOTONERA_SPECS[fam];
  }

  /** Qué se va a renderizar cuando la categoría no elige botonera. */
  get textoHerencia(): string {
    if (this.templateCustom) {
      return `La pantalla la dibuja el template ${this.templateCustom.label}, escrito a mano en la app.`;
    }
    const fam = this.clienteFormValue?.inicioTemplate as BotoneraTemplateId;
    if (fam && BOTONERA_SPECS[fam]) {
      return `Usa la botonera del cliente: ${BOTONERA_SPECS[fam].label}.`;
    }
    return 'La app mobile resuelve el layout por el bundle ID del cliente.';
  }

  /**
   * Quedó una botonera genérica guardada en la categoría de un cliente con template propio: en el
   * nativo la categoría gana, así que ESO le está reemplazando la pantalla hecha a mano. Dato
   * viejo que hay que limpiar (el chooser está bloqueado, así que se ofrece un botón).
   */
  get pisaTemplateCustom(): boolean {
    return this.bloqueado && !!this.botoneraPropia;
  }

  /** Variantes que distingue el template del cliente (`categoria.nombreTemplate`). */
  get variantes(): string[] {
    return this.templateCustom?.variantes ?? [];
  }
  /** Cantidad de botones en pantalla 1: define el tamaño recomendado de imagen del botón. */
  get cantidadPrincipales(): number {
    return this.editable.idsPantalla1.length || 1;
  }
  get nombreValido(): boolean {
    return !!this.nombreCliente;
  }

  /**
   * Slots de imagen override del NATIVO. Con una botonera genérica salen de su spec (con el tamaño
   * recomendado); con un template personalizado no se puede saber cuáles usa, así que se ofrecen
   * los siete: el nativo aplica `cliente.categorias[].imagenX` sea el template que sea
   * (`InicioViewModel`), y esconderlos dejaba sin editar las imágenes de los clientes custom.
   */
  get slotsNativos(): SlotImagen[] {
    const fam = this.botoneraId;
    if (!fam || !BOTONERA_SPECS[fam]) return this.slotsSinSpec();
    const out: SlotImagen[] = [];
    const vistos = new Set<string>();
    for (const s of BOTONERA_SPECS[fam].slots) {
      // urlImagen* es del botón, no de la categoría.
      if (s.slot.startsWith('urlImagen')) continue;
      if (!(s.slot in SLOTS_NATIVOS)) continue;
      if ((s.slot === 'reflector' || s.slot === 'sirena') && !this.sirenas) {
        continue;
      }
      if (vistos.has(s.slot)) continue;
      vistos.add(s.slot);
      out.push({ control: s.slot, label: s.label, hint: textoHint(s) });
    }
    return out;
  }

  /**
   * Los siete overrides, sin hint de tamaño (el template personalizado no declara specs). Los que
   * el template no dibuja se muestran igual —el dato se guarda y puede venir de antes— pero
   * marcados: cargar una imagen ahí no tiene ningún efecto en la app.
   */
  private slotsSinSpec(): SlotImagen[] {
    return Object.keys(SLOTS_NATIVOS)
      .filter(
        (slot) =>
          this.sirenas || (slot !== 'sirena' && slot !== 'reflector'),
      )
      .map((slot) => ({
        control: slot,
        label: LABELS_SIN_SPEC[slot] ?? slot,
        hint: '',
        sinUso: !templateUsaSlot(this.templateCustom, slot),
      }));
  }

  /** ¿Hay imagen cargada en un slot que el template ignora? Es el caso que conviene mirar. */
  public cargadaYSinUso(slot: SlotImagen): boolean {
    return !!slot.sinUso && !!this.editable.imagenes.get(slot.control)?.value;
  }

  /** Cuántos de los slots visibles no los usa el template. Alimenta la nota del encabezado. */
  get cantidadSinUso(): number {
    return this.slotsNativos.filter((s) => s.sinUso).length;
  }

  /** Preview de ESTA categoría: base del cliente + su botonera + sus imágenes override. */
  get previewVm(): PreviewVM {
    const base = this.clienteFormValue ?? {};
    const overrides: Record<string, string> = {};
    const propias = this.editable.imagenes?.value ?? {};
    for (const [slot, val] of Object.entries(propias)) {
      if (val) overrides[slot] = val as string;
    }
    const formValue = {
      ...base,
      inicioTemplate: this.botoneraId,
      sirenas: this.sirenas,
      imagenes: { ...(base.imagenes ?? {}), ...overrides },
    };
    return construirPreviewVM(
      formValue,
      this.editable.botones?.value ?? [],
      this.editable.idsPantalla1,
      this.editable.idsPantalla3,
      this.editable.idsPantalla2,
    );
  }

  /** `null` = sin plantilla propia: hereda la del cliente (o la custom anclada al bundleId). */
  public setBotonera(id: string | null): void {
    // Con template hecho a mano no hay nada que elegir: el chooser está bloqueado.
    if (this.bloqueado && id) return;
    // Primera vez en "a mano": arranca con el reparto actual armado como la botonera banner.
    if (
      id === ID_PLANTILLA_MANUAL &&
      !pantallasDe(this.plantillaManual).some((p) => p.bloques?.length)
    ) {
      this.grupo?.patchValue({
        plantillaBotonera: plantillaInicial(
          this.editable.idsPantalla1,
          this.editable.idsPantalla2,
          this.editable.idsPantalla3,
        ),
      });
      this.pantallaManual = 0;
    }
    this.grupo?.patchValue({ inicioTemplate: id });
    this.grupo?.markAsDirty();
  }

  /** Limpia la botonera genérica que quedó guardada sobre un template hecho a mano. */
  public limpiarBotonera(): void {
    this.grupo?.patchValue({ inicioTemplate: null });
    this.grupo?.markAsDirty();
  }

  // ===================== Botones =====================

  /** Agrega un botón NUEVO (se crea al guardar) y lo pone en pantalla 1. */
  public agregarBoton(): void {
    const tmpId = `${TMP_BTN}${this.editable.idCategoria}-${++this.contadorTmp}`;
    this.editable.botones.push(crearGrupoBoton(this.fb, tmpId));
    this.editable.idsPantalla1.push(tmpId);
  }

  /**
   * Convierte un botón nuevo (en blanco) en una REFERENCIA a uno ya creado: reemplaza el
   * FormGroup por uno con `_tmpId` = `_id` real, manteniendo su posición en la pantalla.
   * Los botones existentes NO se editan desde acá (se editan en el CRUD de Botones).
   */
  public usarExistente(
    index: number,
    boton: IBoton | undefined,
    select: NgSelectComponent,
  ): void {
    if (!boton?._id) return;
    const yaUsado = this.botonesControls.some(
      (g) => String(g.get('_tmpId')?.value) === boton._id,
    );
    if (yaUsado) {
      this.helper.notifWarn('Ese botón ya está en la categoría');
      select.clearModel();
      return;
    }
    const tmpId = String(this.botonesControls[index]?.get('_tmpId')?.value);
    this.editable.botones.setControl(
      index,
      crearGrupoBoton(this.fb, boton._id, boton),
    );
    const reemplazar = (arr: string[]): void => {
      const i = arr.indexOf(tmpId);
      if (i >= 0) arr[i] = boton._id!;
    };
    reemplazar(this.editable.idsPantalla1);
    reemplazar(this.editable.idsPantalla2);
    reemplazar(this.editable.idsPantalla3);
    // Las piezas de la plantilla manual que lo tenían elegido pasan a apuntar al existente.
    const p = this.grupo?.get('plantillaBotonera')?.value;
    if (p) {
      this.grupo.patchValue({
        plantillaBotonera: reemplazarBoton(p, tmpId, boton._id),
      });
    }
  }

  /** Botón existente (referenciado): solo lectura acá. */
  public esExistente(g: UntypedFormGroup): boolean {
    return !String(g.get('_tmpId')?.value).startsWith(TMP_BTN);
  }

  public searchBoton = (term: string, item: IBoton): boolean => {
    const t = term.toLowerCase();
    if (item.nombre?.toLowerCase().includes(t)) return true;
    if (item.variante?.toLowerCase().includes(t)) return true;
    return false;
  };

  /** Saca el botón de la categoría. Si era existente, solo se quita la referencia. */
  public eliminarBoton(index: number): void {
    const tmpId = this.botonesControls[index]?.get('_tmpId')?.value;
    this.editable.botones.removeAt(index);
    const quitar = (arr: string[]): void => {
      const i = arr.indexOf(tmpId);
      if (i >= 0) arr.splice(i, 1);
    };
    quitar(this.editable.idsPantalla1);
    quitar(this.editable.idsPantalla2);
    quitar(this.editable.idsPantalla3);
    // Y de las piezas de la plantilla manual.
    const p = this.grupo?.get('plantillaBotonera')?.value;
    if (p) {
      this.grupo.patchValue({
        plantillaBotonera: reemplazarBoton(p, String(tmpId), null),
      });
    }
  }

  // ===================== Imágenes override =====================

  /** Sube una imagen override de la categoría (clave única por categoría + slot). */
  public async setFile(
    event: Event,
    control: string,
    legacy = false,
  ): Promise<void> {
    if (!this.nombreValido) {
      this.helper.notifWarn('Primero poné el nombre del cliente');
      return;
    }
    const input = (event as any).target as HTMLInputElement | null;
    const file = input?.files?.item(0);
    // Limpio el input: si no, volver a elegir el mismo archivo no dispara `change`.
    if (input) input.value = '';
    if (!file) return;
    await this.subir(file, control, legacy);
  }

  /**
   * Fondo de pantalla de UN color: genera una imagen lisa de ese color y la sube como el slot
   * `fondo`. La app ya dibuja la imagen de fondo a pantalla completa (`MarcoFondo`, Crop), así que
   * se ve como un color de fondo sin tocar el nativo.
   */
  public async setColorFondo(color: string): Promise<void> {
    // ngx-colors emite `close` también al cancelar o al clickear afuera: solo subo si se eligió algo.
    if (!this.fondoCambiado) return;
    this.fondoCambiado = false;
    const hex = aHex(color);
    if (!hex) return;
    if (!this.nombreValido) {
      this.helper.notifWarn('Primero poné el nombre del cliente');
      return;
    }
    const canvas = document.createElement('canvas');
    // Proporción de teléfono: con Crop cualquier tamaño sirve; liso, el PNG pesa unos KB.
    canvas.width = 1080;
    canvas.height = 2400;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = hex;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((ok) =>
      canvas.toBlob(ok, 'image/png'),
    );
    if (!blob) return;
    const file = new File([blob], `fondo-${hex.slice(1)}.png`, {
      type: 'image/png',
    });
    // El color va en la query (además de romper la caché): así el selector arranca en el actual.
    await this.subir(file, 'fondo', false, `color=${hex.slice(1)}`);
  }

  /** Se eligió un color en el selector de fondo abierto (se resetea al abrirlo). */
  public fondoCambiado = false;

  /** Color inicial del selector de fondo: el de la imagen lisa actual, si la generamos acá. */
  get colorFondoActual(): string {
    const url: string = this.editable.imagenes.get('fondo')?.value ?? '';
    const m = /[?&]color=([0-9a-f]{6})(?![0-9a-f])/i.exec(url);
    return m ? `#${m[1]}` : '#000f26';
  }

  private async subir(
    file: File,
    control: string,
    legacy = false,
    query = `${(Math.random() * 1000).toFixed(0)}`,
  ): Promise<void> {
    this.loading = true;
    try {
      const res = await firstValueFrom(
        this.clientesService.subirImagen(
          file,
          this.nombreCliente,
          control,
          carpetaCategoria(
            this.grupo?.get('nombre')?.value,
            this.editable.idCategoria,
          ),
        ),
      );
      const val = `${res.url}?${query}`;
      const destino = legacy
        ? this.editable.imagenesLegacy
        : this.editable.imagenes;
      destino.patchValue({ [control]: val });
      // El chequeo de guardado mira `pristine`; patchValue no lo marca.
      destino.markAsDirty();
    } catch (err) {
      console.error(err);
      this.helper.notifError(err);
    }
    this.loading = false;
  }

  /** Borra el override: la categoría vuelve a usar la imagen base del cliente. */
  public limpiarSlot(control: string, legacy = false): void {
    const destino = legacy
      ? this.editable.imagenesLegacy
      : this.editable.imagenes;
    destino.patchValue({ [control]: null });
    destino.markAsDirty();
  }

  /** Cuántas imágenes legacy hay cargadas (badge del acordeón). */
  get cantidadLegacy(): number {
    const v = this.editable.imagenesLegacy?.value ?? {};
    return Object.values(v).filter(Boolean).length;
  }
}

/** Lo que devuelve ngx-colors (hex, a veces con alfa, o rgb[a]) como `#rrggbb`, o `null`. */
function aHex(color?: string | null): string | null {
  const c = (color ?? '').trim();
  const h = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(c);
  if (h) return `#${h[1].toLowerCase()}`;
  const r = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(c);
  if (!r) return null;
  return (
    '#' +
    r
      .slice(1, 4)
      .map((n) => Math.min(255, Number(n)).toString(16).padStart(2, '0'))
      .join('')
  );
}
